import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Button } from "../../../components/SystemComponent/Button";
import { createApi } from "../../../../core/api";
import { MdCode, MdVisibility, MdEditNote } from "react-icons/md";

export const EmailTemplateEdit = () => {
  const api = useMemo(() => createApi(), []);
  const navigate = useNavigate();
  const { id } = useParams();

  const templateId = Number(id);
  const isCreate = templateId === 0;

  const [code, setCode] = useState("");
  const [subject, setSubject] = useState("");
  const [placeholders, setPlaceholders] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"html" | "preview" | "visual">("html");

  useEffect(() => {
    if (isCreate) return;

    api.emailTemplate.getById(templateId).then((r) => {
      if (r?.ok) {
        const d = r.data?.data ?? r.data;
        setCode(d.code || "");
        setSubject(d.subject || "");
        setPlaceholders(d.placeholders || "");
        setContent(d.content || "");
      }
    });
  }, [templateId, isCreate, api]);

  const submit = async () => {
    if (!code || !subject || saving) return;

    setSaving(true);
    const payload = { code, subject, placeholders, content };

    if (isCreate) {
      await api.emailTemplate.create(payload);
    } else {
      await api.emailTemplate.update(templateId, payload);
    }

    navigate("/dashboard/email-template");
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>
          {isCreate ? "Create Email Template" : "Edit Email Template"}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <input
          placeholder="Code (e.g. USER_WELCOME)"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={input}
        />
        <input
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={input}
        />
        <input
          placeholder="Placeholders (e.g. {FullName}, {VerificationLink})"
          value={placeholders}
          onChange={(e) => setPlaceholders(e.target.value)}
          style={{ ...input, gridColumn: "1 / -1" }}
        />
      </div>

      {/* Editor Tabs: HTML Source, Live Preview, Visual */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, borderBottom: "1px solid #E2E8F0", paddingBottom: 8 }}>
        <button
          type="button"
          onClick={() => setActiveTab("html")}
          style={{
            ...tabButton,
            background: activeTab === "html" ? "#2563EB" : "#F1F5F9",
            color: activeTab === "html" ? "#FFF" : "#475569",
            fontWeight: activeTab === "html" ? 600 : 500,
          }}
        >
          <MdCode size={18} />
          HTML & CSS Kodu (Kaynak)
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("preview")}
          style={{
            ...tabButton,
            background: activeTab === "preview" ? "#059669" : "#F1F5F9",
            color: activeTab === "preview" ? "#FFF" : "#475569",
            fontWeight: activeTab === "preview" ? 600 : 500,
          }}
        >
          <MdVisibility size={18} />
          Stilli Canlı Önizleme
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("visual")}
          style={{
            ...tabButton,
            background: activeTab === "visual" ? "#4F46E5" : "#F1F5F9",
            color: activeTab === "visual" ? "#FFF" : "#475569",
            fontWeight: activeTab === "visual" ? 600 : 500,
          }}
        >
          <MdEditNote size={18} />
          Görsel Editör
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === "html" && (
        <div>
          <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>
            HTML ve inline CSS kodlarını doğrudan buraya yapıştırabilirsiniz. Tüm stiller, etiketler ve renkler birebir korunur.
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="<!DOCTYPE html><html>... E-posta HTML ve CSS kodunuzu buraya yapıştırın ...</html>"
            style={{
              width: "100%",
              height: 520,
              padding: 16,
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: 13,
              lineHeight: 1.5,
              borderRadius: 8,
              border: "1px solid #CBD5E1",
              background: "#0F172A",
              color: "#E2E8F0",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </div>
      )}

      {activeTab === "preview" && (
        <div>
          <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>
            E-postanın CSS stilleriyle birlikte tam e-posta istemcisi gibi canlı render edilmiş görünümü:
          </div>
          <div
            style={{
              width: "100%",
              height: 550,
              borderRadius: 8,
              border: "1px solid #CBD5E1",
              background: "#FFF",
              overflow: "hidden",
            }}
          >
            <iframe
              title="Email Preview"
              srcDoc={content || "<div style='padding:20px;font-family:sans-serif;color:#888;'>Henüz içerik girilmedi.</div>"}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#FFF",
              }}
              sandbox="allow-same-origin allow-popups"
            />
          </div>
        </div>
      )}

      {activeTab === "visual" && (
        <div>
          <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6 }}>
            Görsel zengin metin editörü (basit metin ve düzenlemeler için):
          </div>
          <div style={{ minHeight: 400, border: "1px solid #CBD5E1", borderRadius: 8, overflow: "hidden" }}>
            <CKEditor
              editor={ClassicEditor}
              data={content}
              onChange={(_, editor) => {
                setContent(editor.getData());
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 20,
        }}
      >
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard/email-template")}
        >
          Cancel
        </Button>
        <Button onClick={submit} loading={saving}>
          Save Template
        </Button>
      </div>
    </div>
  );
};

const input: React.CSSProperties = {
  height: 42,
  borderRadius: 8,
  border: "1px solid #CBD5E1",
  padding: "0 12px",
  fontSize: 14,
};

const tabButton: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 13,
  transition: "all 0.2s",
};
